import './PasswordField.scss';
import '../../PermissionsList/ShareLink/ShareLink.scss';
import { PasswordStrengthMeter } from '../PasswordStrengthMeter/PasswordStrengthMeter';
import { TextField } from 'office-ui-fabric-react/lib/TextField';
import { Icon, IIconProps } from 'office-ui-fabric-react/lib/Icon';
import {
    ISharingLinkTemplate
} from '../../../interfaces/SharingInterfaces';
import stringsResx from '../../Share.resx';
import FeatureOverrides from '@ms/odsp-utilities/lib/features/FeatureOverrides';
import * as React from 'react';

// Features
const PasswordStrengthMeterUI = { ODB: 1204 };

export interface IPasswordFieldState {
    currentPassword: string;
    disablePasswordField: boolean;
    displayRemovePasswordButton: boolean;
    displayNotificationText: boolean;
    hasExistingPassword: boolean;
    matchingTemplate: ISharingLinkTemplate;
}

export interface IPasswordFieldProps {
    onPasswordChange: (newPassword: string) => void;
    detailsPane?: boolean;
    getShareLinkTemplate?: (requiresPassword: boolean, updateShareId: boolean) => ISharingLinkTemplate;
    lastModifiedBy?: string; // Only used from the password details pane to manually pass in the last set by name
}

export class PasswordField extends React.Component<IPasswordFieldProps, IPasswordFieldState> {
    constructor(props: IPasswordFieldProps, context: any) {
        super(props);
        if (this.props.detailsPane) {
            let defaultPassword = '************';
            this.state = {
                currentPassword: defaultPassword,
                disablePasswordField: true,
                displayRemovePasswordButton: true,
                displayNotificationText: true,
                matchingTemplate: null,
                hasExistingPassword: true
            };
        } else {
            this._getInitialShareLinkTemplate();
        }
        this._getInitialShareLinkTemplate = this._getInitialShareLinkTemplate.bind(this);
        this._updateShareLinkTemplate = this._updateShareLinkTemplate.bind(this);
        this._renderShareReminder = this._renderShareReminder.bind(this);
        this._onFocusPasswordFieldDetails = this._onFocusPasswordFieldDetails.bind(this);
        this._onBlurPasswordField = this._onBlurPasswordField.bind(this);
        this._onUpdatePassword = this._onUpdatePassword.bind(this);
        this._onDeletePassword = this._onDeletePassword.bind(this);
    }

    public render(): React.ReactElement<{}> {
        if (!this.props.detailsPane) {
            this._updateShareLinkTemplate();
        }
        const iconProps: IIconProps = (this.state.displayRemovePasswordButton) ?
            {
                iconName: 'Cancel',
                onClick: this._onDeletePassword,
                className: 'od-PasswordField-delete',
                ariaLabel: stringsResx.removePassword
            } : undefined;
        return (
            <div className='od-PasswordField'>
                <div className='od-PasswordField-passwordFieldLine'>
                    <div className='od-PasswordField-label'>
                        <Icon iconName='Lock' />
                    </div>
                    <div className='od-ShareLink-passwordFieldContent'>
                        <TextField
                            ref='passwordField'
                            type='password'
                            autoComplete='off'
                            disabled={ this.state.disablePasswordField }
                            placeholder={ stringsResx.setPasswordFieldText }
                            value={ this.state.currentPassword }
                            onChanged={ this._onUpdatePassword }
                            onFocus={ this._onFocusPasswordFieldDetails }
                            onBlur={ this._onBlurPasswordField }
                            borderless
                            iconProps={ iconProps }
                        />
                    </div>
                </div>
                { this._renderShareReminder() }
            </div>
        );
    }

    /**
     * Check whether we have a template with a password and instantiate the initial state of the password field
     */
    private _getInitialShareLinkTemplate() {
        // We only want to see if there's an existing password template, so we don't want to update the shareId
        const currentTemplate = this.props.getShareLinkTemplate(true, false);
        // If we can't find a template, assume no password exists.
        if (currentTemplate && currentTemplate.linkDetails) { // Link exists and is password protected
            this.state = {
                ...this.state,
                currentPassword: '************',
                disablePasswordField: true,
                displayRemovePasswordButton: true,
                displayNotificationText: true,
                matchingTemplate: currentTemplate,
                hasExistingPassword: true
            };
        } else {
            this.state = { // No link, or not password protected
                ...this.state,
                currentPassword: '',
                disablePasswordField: false,
                displayRemovePasswordButton: false,
                displayNotificationText: false,
                matchingTemplate: currentTemplate,
                hasExistingPassword: false
            };
        }
    }

    private _updateShareLinkTemplate() {
        const currentTemplate = this.props.getShareLinkTemplate(true, false);
        // Only update template if the template has changed
        if (currentTemplate !== this.state.matchingTemplate) {
            // If we can't find a template, assume no password exists.
            if (currentTemplate && currentTemplate.linkDetails) { // Link exists and is password protected
                this.setState({
                    ...this.state,
                    currentPassword: '************',
                    disablePasswordField: true,
                    displayRemovePasswordButton: true,
                    displayNotificationText: true,
                    matchingTemplate: currentTemplate,
                    hasExistingPassword: true
                });
            } else {
                this.setState({ // No link, or not password protected
                    ...this.state,
                    currentPassword: '',
                    disablePasswordField: false,
                    displayRemovePasswordButton: false,
                    displayNotificationText: false,
                    matchingTemplate: currentTemplate,
                    hasExistingPassword: false
                });
            }
        }
    }

    /**
     * May become password field in the future
     */
    private _renderShareReminder() {
        let shareReminderDivName = 'od-PasswordField-shareReminder';
        if (this.props.detailsPane) {
            // CSS Formating is slightly different for detailsPane
            shareReminderDivName += '-detailsPane';
        }
        if (this.state.displayNotificationText) {
            if (this.state.hasExistingPassword) {
                if (this.props.detailsPane) {
                    return (
                        <div className='odPasswordField-lastModifiedBy'>
                            <div className={ shareReminderDivName } >
                                { stringsResx.passwordFieldPasswordSetBy + this.props.lastModifiedBy }
                            </div>
                        </div>
                    );
                } else {
                    return (
                        <div className='odPasswordField-lastModifiedBy'>
                            <div className={ shareReminderDivName } >
                                { stringsResx.passwordFieldPasswordSetBy + this.state.matchingTemplate.linkDetails.passwordLastModifiedBy.primaryText }
                            </div>
                        </div>
                    );
                }
            } else {
                const showPasswordStrengthMeter = FeatureOverrides.isFeatureEnabled(PasswordStrengthMeterUI);
                if (showPasswordStrengthMeter && this.state.currentPassword.length > 0) {
                    return (
                        <div className={ shareReminderDivName } >
                            <PasswordStrengthMeter
                                currentPassword={ this.state.currentPassword }
                            />
                        </div>
                    );
                } else {
                    return (
                        <div className={ shareReminderDivName } >
                            { stringsResx.passwordFieldShareReminder }
                        </div>
                    );
                }
            }
        } else {
            return (
                <div className={ shareReminderDivName } >
                    { '' }
                </div>
            );
        }
    }

    /**
     * When we focus on the Password Field we want to clear the current password for the user to type
     */
    private _onFocusPasswordFieldDetails() {
        this.setState({
            ...this.state,
            currentPassword: '',
            displayNotificationText: true,
            displayRemovePasswordButton: false,
            hasExistingPassword: false
        }, () => {
            this.props.onPasswordChange('');
        });
    }

    /**
     * When we focus on the Password Field we don't want to display the notification text
     */
    private _onBlurPasswordField() {
        this.setState({
            ...this.state,
            displayNotificationText: false
        });
    }

    /**
     * When we have something in the password field, display the remove password button
     * @param newPassword
     */
    private _onUpdatePassword(newPassword: string) {
        const isNotEmptyString = newPassword.length > 0;
        this.setState({
            ...this.state,
            currentPassword: newPassword,
            displayRemovePasswordButton: isNotEmptyString
        }, () => {
            this.props.onPasswordChange(newPassword);
        });
    }

    /**
     * When we delete a preexisting password, remove the displayRemovePasswordButton
     */
    private _onDeletePassword(): void {
        this.setState({
            ...this.state,
            currentPassword: '',
            disablePasswordField: false,
            displayRemovePasswordButton: false,
            hasExistingPassword: false
        }, () => {
            this.props.onPasswordChange('');
        });
    }
}